import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LogOut, Menu, Monitor, Moon, PanelLeft, Sun } from "lucide-react";
import { api, type Catalog, type Session } from "./api";
import { IconButton, Modal } from "./components/ui";
import { useTheme, THEME_ACTION } from "./hooks/useTheme";
import type { ThemeChoice } from "./hooks/usePreferences";
import { SignIn } from "./pages/SignIn";
import { Studio } from "./pages/Studio";
import { LibraryPage } from "./pages/Library";
import { ModelGardenPage } from "./pages/ModelGarden";
import { DocsPage } from "./pages/Docs";
import { AnalyticsPage } from "./pages/Analytics";
import { AdminPage } from "./pages/Admin";

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

const THEME_CHOICES: { value: ThemeChoice; label: string }[] = [
  { value: "system", label: "System" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

function themeIcon(choice: ThemeChoice) {
  if (choice === "system") return <Monitor size={15} />;
  return choice === "dark" ? <Moon size={15} /> : <Sun size={15} />;
}

/**
 * Owns the session and the catalog, and nothing else (IMPLEMENTATION_PLAN.md §11.3).
 *
 * Both are fetched once, at the top, and the catalog is handed to Studio and the Model Garden as
 * a prop. Two values shared by three pages do not justify a context, let alone a store.
 */
export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const theme = useTheme();
  const location = useLocation();

  // Both are phone-only: the boards rail slides over the workspace, and the section links do not
  // fit beside the wordmark and the account controls at 320px (§21.1).
  const [railOpen, setRailOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .session(controller.signal)
      .then(setSession)
      .catch(() => {
        // A failed session check is indistinguishable from being signed out, and the sign-in page
        // is the only useful thing to show either way.
        if (!controller.signal.aborted) setSession({ authenticated: false });
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!session?.authenticated) return;
    const controller = new AbortController();
    api
      .catalog(controller.signal)
      .then(setCatalog)
      .catch(() => {
        // Studio reports the missing catalog where the composer would be; the rest of the app
        // still works without it.
      });
    return () => controller.abort();
  }, [session?.authenticated]);

  // Studio is two routes, so the nav link points at whichever one you were last on. Clicking
  // "Studio" from the library takes you back to the board you were reading, not to the top.
  const [studioPath, setStudioPath] = useState("/");
  useEffect(() => {
    if (location.pathname === "/" || location.pathname.startsWith("/b/")) {
      setStudioPath(location.pathname);
    }
  }, [location.pathname]);

  // Arriving somewhere new closes whatever was covering the screen to get there.
  useEffect(() => {
    setRailOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!railOpen) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setRailOpen(false);
    };
    // Above the phone breakpoint the rail is a permanent column, where "open" and its scrim are
    // both meaningless. Rotating a tablet must not leave a dead scrim over the workspace.
    const phone = window.matchMedia("(max-width: 760px)");
    const onWidthChange = () => {
      if (!phone.matches) setRailOpen(false);
    };

    document.addEventListener("keydown", onKey);
    phone.addEventListener("change", onWidthChange);
    return () => {
      document.removeEventListener("keydown", onKey);
      phone.removeEventListener("change", onWidthChange);
    };
  }, [railOpen]);

  if (session === null) {
    // The session is unknown for one round trip. Anything rendered here would only flash.
    return <div className="app" />;
  }

  if (!session.authenticated) {
    return <SignIn />;
  }

  const user = session.user;
  const isAdmin = user?.is_admin === true;
  const onStudio = location.pathname === "/" || location.pathname.startsWith("/b/");

  const navItems: NavItem[] = [
    { to: studioPath, label: "Studio", end: true },
    { to: "/library", label: "Library" },
    { to: "/models", label: "Models" },
    { to: "/analytics", label: "Analytics" },
    { to: "/docs", label: "Docs" },
  ];
  if (isAdmin) navItems.push({ to: "/admin", label: "Admin" });

  return (
    <div className="app">
      <header className="masthead">
        {onStudio && (
          <span className="show-mobile">
            <IconButton
              onClick={() => setRailOpen((open) => !open)}
              label={railOpen ? "Hide the boards" : "Show the boards"}
            >
              <PanelLeft size={16} />
            </IconButton>
          </span>
        )}

        <Link to="/" className="masthead-brand">
          <span className="lamp" aria-hidden />
          <span className="wordmark">Darkroom</span>
        </Link>

        <nav className="masthead-nav" aria-label="Sections">
          {navItems.map((item) => (
            // Hidden on a phone rather than squeezed: six links, a wordmark and the account
            // controls do not share 320px. The menu below carries the same list.
            <NavLink key={item.label} to={item.to} end={item.end} className="nav-link hide-mobile">
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="masthead-user">
          {user?.picture_url && <img className="avatar" src={user.picture_url} alt="" />}
          <span className="masthead-email">{user?.email}</span>

          <span className="hide-mobile">
            <IconButton onClick={theme.cycle} label={THEME_ACTION[theme.choice]}>
              {themeIcon(theme.choice)}
            </IconButton>
          </span>

          <span className="show-mobile">
            <IconButton onClick={() => setMenuOpen(true)} label="Menu">
              <Menu size={16} />
            </IconButton>
          </span>

          <IconButton
            onClick={() => {
              // A full reload after sign-out, so no page keeps a copy of the previous user's work.
              void api.logout().finally(() => {
                window.location.href = "/";
              });
            }}
            label="Sign out"
          >
            <LogOut size={15} />
          </IconButton>
        </div>
      </header>

      <Routes>
        <Route
          path="/"
          element={
            <Studio catalog={catalog} railOpen={railOpen} onCloseRail={() => setRailOpen(false)} />
          }
        />
        <Route
          path="/b/:boardId"
          element={
            <Studio catalog={catalog} railOpen={railOpen} onCloseRail={() => setRailOpen(false)} />
          }
        />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/models" element={<ModelGardenPage catalog={catalog} />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        {/* Admin is gated in the router as well as on the server: a redirect is a clearer answer
            than a page of 403s, and the server is still the one enforcing it. */}
        <Route path="/admin" element={isAdmin ? <AdminPage /> : <Navigate to="/" replace />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/docs/*" element={<DocsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {menuOpen && (
        <Modal title="Menu" onClose={() => setMenuOpen(false)}>
          <div className="list">
            {navItems.map((item) => (
              <NavLink
                key={item.label}
                to={item.to}
                end={item.end}
                className={({ isActive }) => (isActive ? "list-row is-selected" : "list-row")}
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </NavLink>
            ))}
          </div>

          <div className="field">
            <span className="field-label">Theme</span>
            <div className="segmented">
              {THEME_CHOICES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={theme.choice === option.value ? "is-selected" : undefined}
                  aria-pressed={theme.choice === option.value}
                  onClick={() => theme.setChoice(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
